/**
 * GatheringUI.jsx - Gather button and resource yield display
 */

import React, { useState, useEffect, useRef } from 'react';
import { resources } from '../../../data/resources';

// Helper to get resource icon from canonical source
const getResourceIcon = (resourceId) => resources[resourceId]?.icon || '📦';

// Format amount with 1 decimal if not whole number
const formatAmount = (amount) => {
  if (Number.isInteger(amount)) return amount.toString();
  return amount.toFixed(1);
};

export function GatheringUI({
  expectedYields,
  tileQuality,
  gatherOnCooldown,
  onGather,
  autoGatherEnabled,
  onToggleAutoGather,
  lastGatherTime
}) {
  const [bounceKey, setBounceKey] = useState(0);
  const prevGatherTimeRef = useRef(lastGatherTime);
  const lastManualGatherRef = useRef(0);

  // Trigger bounce animation when auto-gather happens
  useEffect(() => {
    if (autoGatherEnabled && lastGatherTime && lastGatherTime !== prevGatherTimeRef.current) {
      setBounceKey(prev => prev + 1);
      prevGatherTimeRef.current = lastGatherTime;
    }
  }, [lastGatherTime, autoGatherEnabled]);

  // Handle manual gather with bounce animation and anti-autoclicker throttle
  const handleManualGather = () => {
    const now = Date.now();
    const timeSinceLastGather = now - lastManualGatherRef.current;

    // Throttle: minimum 50ms between manual gathers (20 clicks/second max)
    if (timeSinceLastGather < 50) {
      return;
    }

    lastManualGatherRef.current = now;
    setBounceKey(prev => prev + 1);
    onGather();
  };

  if (!expectedYields?.yields || Object.keys(expectedYields.yields).length === 0) {
    return null;
  }

  const lifeSkillBonuses = expectedYields.lifeSkillBonuses || {};
  const hasAnyBonus = Object.values(lifeSkillBonuses).some(b => b > 0);

  return (
    <div className="wm-gather-panel">
      {/* Header with tile quality */}
      <div className="wm-gather-header">
        <span className="wm-gather-title">⛏️ Gather</span>
        {tileQuality && (
          <span className={`wm-tile-quality-badge ${tileQuality >= 1.1 ? 'high' : tileQuality <= 0.9 ? 'low' : ''}`}>
            Tile: {Math.round(tileQuality * 100)}%
          </span>
        )}
      </div>

      {/* Resource yields list */}
      <div className="wm-gather-yields">
        {Object.entries(expectedYields.yields).map(([r, a]) => {
          const bonus = lifeSkillBonuses[r];
          return (
            <div key={r} className="wm-gather-yield-row">
              <span className="wm-gather-resource">
                <span className="wm-gather-icon">{getResourceIcon(r)}</span>
                <span className="wm-gather-name">{r}</span>
              </span>
              <span className="wm-gather-values">
                <span className="wm-gather-amount">+{formatAmount(a)}</span>
                {bonus > 0 && (
                  <span className="wm-gather-bonus">+{Math.round(bonus * 100)}%</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Gather buttons */}
      <div className="wm-gather-buttons">
        <button
          key={bounceKey}
          className={`wm-btn ${autoGatherEnabled ? 'auto-gather' : 'primary'} bounce`}
          onClick={handleManualGather}
          disabled={autoGatherEnabled}
        >
          Gather
        </button>
        <button
          className={`wm-btn-toggle ${autoGatherEnabled ? 'active' : ''}`}
          onClick={onToggleAutoGather}
          title={autoGatherEnabled ? 'Disable auto-gather' : 'Enable auto-gather'}
        >
          {autoGatherEnabled ? '⏸️' : '▶️'}
        </button>
      </div>
    </div>
  );
}

export default GatheringUI;
